import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { saveAs } from 'file-saver';

// Export glucose logs to PDF
export const exportGlucoseToPDF = (glucoseLogs, userName = 'User') => {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(20);
  doc.setTextColor(16, 185, 129); // Emerald color
  doc.text('DiaWise - Blood Sugar Report', 20, 20);
  
  doc.setFontSize(12);
  doc.setTextColor(100);
  doc.text(`Generated for: ${userName}`, 20, 30);
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 37);
  
  // Stats summary
  if (glucoseLogs.length > 0) {
    const values = glucoseLogs.map(g => g.value || g.glucose_level);
    const avg = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text('Summary', 20, 50);
    
    doc.setFontSize(11);
    doc.text(`Total Readings: ${glucoseLogs.length}`, 20, 60);
    doc.text(`Average: ${avg} mg/dL`, 20, 67);
    doc.text(`Range: ${min} - ${max} mg/dL`, 20, 74);
  }
  
  // Table
  const tableData = glucoseLogs.map(log => [
    new Date(log.timestamp || log.date).toLocaleDateString(),
    new Date(log.timestamp || log.date).toLocaleTimeString(),
    `${log.value || log.glucose_level} mg/dL`,
    log.reading_type || log.meal_context || '-',
    log.notes || '-'
  ]);
  
  doc.autoTable({
    startY: 85,
    head: [['Date', 'Time', 'Glucose', 'Context', 'Notes']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [16, 185, 129] },
    styles: { fontSize: 9 }
  });
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Page ${i} of ${pageCount} - Generated by DiaWise`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }
  
  doc.save(`diawise-glucose-report-${new Date().toISOString().split('T')[0]}.pdf`);
};

// Export meals to PDF
export const exportMealsToPDF = (meals, userName = 'User') => {
  const doc = new jsPDF();
  
  doc.setFontSize(20);
  doc.setTextColor(249, 115, 22); // Orange color
  doc.text('DiaWise - Meal Report', 20, 20);
  
  doc.setFontSize(12);
  doc.setTextColor(100);
  doc.text(`Generated for: ${userName}`, 20, 30);
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 37);
  
  // Stats
  if (meals.length > 0) {
    const totalCarbs = meals.reduce((sum, m) => sum + (m.carbs || 0), 0);
    const totalCalories = meals.reduce((sum, m) => sum + (m.calories || 0), 0);
    
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text('Summary', 20, 50);
    
    doc.setFontSize(11);
    doc.text(`Total Meals: ${meals.length}`, 20, 60);
    doc.text(`Total Carbs: ${totalCarbs}g`, 20, 67);
    doc.text(`Total Calories: ${totalCalories}`, 20, 74);
  }
  
  const tableData = meals.map(meal => [
    new Date(meal.timestamp || meal.date).toLocaleDateString(),
    meal.meal_type || meal.type || '-',
    meal.name || meal.description || '-',
    `${meal.carbs || 0}g`,
    `${meal.calories || 0}`,
    meal.notes || '-'
  ]);
  
  doc.autoTable({
    startY: 85,
    head: [['Date', 'Type', 'Meal', 'Carbs', 'Calories', 'Notes']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [249, 115, 22] },
    styles: { fontSize: 9 }
  });
  
  doc.save(`diawise-meals-report-${new Date().toISOString().split('T')[0]}.pdf`);
};

// Export to CSV
export const exportToCSV = (data, filename, headers) => {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }
  
  const csvHeaders = headers || Object.keys(data[0]);
  
  const csvContent = [
    csvHeaders.join(','),
    ...data.map(row => 
      csvHeaders.map(header => {
        const value = row[header];
        // Handle values with commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
};

// Export glucose logs to CSV
export const exportGlucoseToCSV = (glucoseLogs) => {
  const formattedData = glucoseLogs.map(log => ({
    date: new Date(log.timestamp || log.date).toLocaleDateString(),
    time: new Date(log.timestamp || log.date).toLocaleTimeString(),
    glucose_level: log.value || log.glucose_level,
    unit: 'mg/dL',
    context: log.reading_type || log.meal_context || '',
    notes: log.notes || ''
  }));
  
  exportToCSV(formattedData, 'diawise-glucose-export', ['date', 'time', 'glucose_level', 'unit', 'context', 'notes']);
};

// Export meals to CSV
export const exportMealsToCSV = (meals) => {
  const formattedData = meals.map(meal => ({
    date: new Date(meal.timestamp || meal.date).toLocaleDateString(),
    meal_type: meal.meal_type || meal.type || '',
    name: meal.name || meal.description || '',
    carbs: meal.carbs || 0,
    calories: meal.calories || 0,
    protein: meal.protein || 0,
    sugar: meal.sugar || 0,
    notes: meal.notes || ''
  }));
  
  exportToCSV(formattedData, 'diawise-meals-export', ['date', 'meal_type', 'name', 'carbs', 'calories', 'protein', 'sugar', 'notes']);
};

// Export full health report
export const exportFullReport = async (glucoseLogs, meals, medications, userName) => {
  const doc = new jsPDF();
  
  // Cover page
  doc.setFontSize(28);
  doc.setTextColor(16, 185, 129);
  doc.text('DiaWise', doc.internal.pageSize.width / 2, 60, { align: 'center' });
  
  doc.setFontSize(20);
  doc.setTextColor(0);
  doc.text('Health Report', doc.internal.pageSize.width / 2, 80, { align: 'center' });
  
  doc.setFontSize(14);
  doc.setTextColor(100);
  doc.text(`Generated for: ${userName}`, doc.internal.pageSize.width / 2, 100, { align: 'center' });
  doc.text(`Date: ${new Date().toLocaleDateString()}`, doc.internal.pageSize.width / 2, 110, { align: 'center' });
  
  // Summary page
  doc.addPage();
  doc.setFontSize(18);
  doc.setTextColor(0);
  doc.text('Summary', 20, 20);
  
  let yPos = 40;
  
  // Glucose summary
  doc.setFontSize(14);
  doc.setTextColor(16, 185, 129);
  doc.text('Blood Sugar', 20, yPos);
  doc.setFontSize(11);
  doc.setTextColor(0);
  doc.text(`Total Readings: ${glucoseLogs?.length || 0}`, 30, yPos + 10);
  
  if (glucoseLogs?.length > 0) {
    const values = glucoseLogs.map(g => g.value || g.glucose_level);
    const avg = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    doc.text(`Average: ${avg} mg/dL`, 30, yPos + 17);
  }
  
  yPos += 35;
  
  // Meals summary
  doc.setFontSize(14);
  doc.setTextColor(249, 115, 22);
  doc.text('Meals', 20, yPos);
  doc.setFontSize(11);
  doc.setTextColor(0);
  doc.text(`Total Meals: ${meals?.length || 0}`, 30, yPos + 10);
  
  yPos += 35;
  
  // Medications summary
  doc.setFontSize(14);
  doc.setTextColor(59, 130, 246);
  doc.text('Medications', 20, yPos);
  doc.setFontSize(11);
  doc.setTextColor(0);
  doc.text(`Active Medications: ${medications?.length || 0}`, 30, yPos + 10);
  
  doc.save(`diawise-full-report-${new Date().toISOString().split('T')[0]}.pdf`);
};
